import { useEffect, useState, useCallback } from "react";
import { Eye, Printer, Truck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatDate, formatFCFA } from "@/lib/format";
import { Loader, StatusBadge } from "@/components/common";
import Modal, { TextField, SelectField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { DELIVERY_STATUS, DELIVERY_STEPS } from "@/lib/labels";
import { toast } from "sonner";

const STATUS_OPTIONS = ["a_preparer", "affectee", "en_preparation", "en_cours", "livree", "echouee", "cloturee"];

export default function AdminDeliveries() {
  const [deliveries, setDeliveries] = useState(null);
  const [tours, setTours] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showTour, setShowTour] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    const [d, t, dr, v] = await Promise.all([
      api.get("/deliveries"), api.get("/tours"),
      api.get("/admin/professionals", { params: { role: "livreur" } }),
      api.get("/admin/vehicles"),
    ]);
    setDeliveries(d.data); setTours(t.data); setDrivers(dr.data); setVehicles(v.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!deliveries) return <Loader />;

  const byZone = {};
  deliveries.filter((d) => d.status === "a_preparer").forEach((d) => { (byZone[d.zone] = byZone[d.zone] || []).push(d); });
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const setStatus = async (did, status) => {
    try { await api.put(`/deliveries/${did}/status`, null, { params: { status } }); toast.success("Statut mis à jour"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageTitle action={selected.length > 0 && <button onClick={() => setShowTour(true)} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg" data-testid="create-tour-btn">Créer une tournée ({selected.length})</button>}>
        Livraisons
      </PageTitle>

      <h2 className="font-display text-lg font-bold mb-2">À planifier — par zone</h2>
      {Object.keys(byZone).length === 0 && <p className="text-sm text-slate-400 mb-4">Aucune livraison à préparer.</p>}
      {Object.entries(byZone).map(([zone, list]) => (
        <div key={zone} className="mb-4">
          <p className="text-sm font-bold text-[#C85A32] mb-1">Zone — {zone} ({list.length})</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {list.map((d) => (
              <label key={d.id} className={`bg-white border-2 rounded-lg p-3 cursor-pointer flex gap-2 items-start ${selected.includes(d.id) ? "border-[#C85A32]" : "border-slate-200"}`} data-testid={`delivery-select-${d.id}`}>
                <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} className="mt-1" />
                <div><p className="font-num font-bold text-sm">{d.order_number}</p><p className="text-xs text-slate-500">{d.customer_name}</p></div>
              </label>
            ))}
          </div>
        </div>
      ))}

      <h2 className="font-display text-lg font-bold mb-2 mt-6">Tournées</h2>
      <div className="space-y-2 mb-8">
        {tours.length === 0 && <p className="text-sm text-slate-400">Aucune tournée.</p>}
        {tours.map((t) => (
          <Panel key={t.id} className="p-4" data-testid={`tour-${t.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-num font-bold">{t.tour_number}</span>
              <StatusBadge map={DELIVERY_STATUS} value={t.status} />
              <span className="text-sm text-slate-500 inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {t.zone} · {t.date && formatDate(t.date)} · {t.driver_name || "Sans chauffeur"} · {[t.vehicle, t.vehicle_plate].filter(Boolean).join(" ") || "Sans véhicule"}</span>
              <span className="ml-auto text-xs text-slate-400">{t.delivery_ids.length} livraison(s)</span>
            </div>
          </Panel>
        ))}
      </div>

      <h2 className="font-display text-lg font-bold mb-2">Toutes les livraisons</h2>
      <Panel className="divide-y divide-slate-100">
        {deliveries.length === 0 && <p className="p-4 text-sm text-slate-400">Aucune livraison.</p>}
        {deliveries.map((d) => (
          <div key={d.id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`delivery-row-${d.id}`}>
            <span className="font-num font-bold text-sm">{d.order_number}</span>
            <span className="text-sm text-slate-500">{d.customer_name} · {d.zone}</span>
            <StatusBadge map={DELIVERY_STATUS} value={d.status} />
            <div className="ml-auto flex items-center gap-2">
              <select value={d.status} onChange={(e) => setStatus(d.id, e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" data-testid={`delivery-status-${d.id}`}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{DELIVERY_STATUS[s].label}</option>)}
              </select>
              <button onClick={() => setDetailId(d.id)} className="p-2 text-slate-500 hover:text-[#C85A32]" data-testid={`delivery-detail-${d.id}`}><Eye className="w-4 h-4" /></button>
              <a href={`/bordereau/${d.id}`} target="_blank" rel="noreferrer" className="p-2 text-slate-500 hover:text-[#C85A32]" data-testid={`delivery-bordereau-${d.id}`}><Printer className="w-4 h-4" /></a>
            </div>
          </div>
        ))}
      </Panel>

      {showTour && <TourModal deliveryIds={selected} drivers={drivers} vehicles={vehicles} zone={deliveries.find((d) => d.id === selected[0])?.zone || ""} onClose={() => setShowTour(false)} onDone={() => { setSelected([]); load(); }} />}
      {detailId && <DeliveryDetail id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function TourModal({ deliveryIds, drivers, vehicles, zone, onClose, onDone }) {
  const [f, setF] = useState({ date: "", zone, vehicle_id: "", driver_id: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/tours", { ...f, delivery_ids: deliveryIds }); toast.success("Tournée créée"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title="Créer une tournée" onClose={onClose} testid="tour-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-500">{deliveryIds.length} livraison(s) sélectionnée(s)</p>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Date" type="date" value={f.date} onChange={set("date")} testid="tour-date" />
          <TextField label="Zone" value={f.zone} onChange={set("zone")} testid="tour-zone" />
        </div>
        <SelectField label="Véhicule" value={f.vehicle_id} onChange={set("vehicle_id")} testid="tour-vehicle"
          options={vehicles.filter((v) => v.status !== "inactif").map((v) => ({ value: v.id, label: `${v.name} · ${v.plate}` }))} placeholder="Choisir un véhicule..." />
        <SelectField label="Chauffeur / Livreur" value={f.driver_id} onChange={set("driver_id")} testid="tour-driver" options={drivers.map((d) => ({ value: d.user_id, label: d.name }))} />
        {vehicles.length === 0 && <p className="text-xs text-amber-600">Aucun véhicule. Créez-en un dans Paramètres › Véhicules.</p>}
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="tour-submit">Créer la tournée</button>
      </form>
    </Modal>
  );
}

function DeliveryDetail({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/deliveries/${id}`).then((r) => setData(r.data)).catch(() => setData(false)); }, [id]);
  return (
    <Modal title="Détail de la livraison" onClose={onClose} testid="delivery-detail-modal" size="lg">
      {!data ? <Loader /> : (
        <div className="space-y-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-num font-bold">{data.delivery.order_number}</span>
            <StatusBadge map={DELIVERY_STATUS} value={data.delivery.status} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {DELIVERY_STEPS.map((s, i) => {
              const done = DELIVERY_STEPS.indexOf(data.delivery.status) >= i;
              return <span key={s} className={`text-[11px] px-2 py-0.5 rounded-full ${done ? "bg-[#C85A32] text-white" : "bg-slate-100 text-slate-400"}`}>{DELIVERY_STATUS[s].label}</span>;
            })}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><p className="text-xs font-bold uppercase text-slate-400 mb-1">Client</p><p className="font-semibold">{data.delivery.customer_name}</p>{data.order?.customer_phone && <p className="text-slate-500">{data.order.customer_phone}</p>}<p className="text-slate-500">{data.delivery.address?.address}</p><p className="text-slate-500">{data.delivery.zone}</p></div>
            <div><p className="text-xs font-bold uppercase text-slate-400 mb-1">Livraison</p><p className="text-slate-500">Chauffeur : {data.delivery.driver_name || "—"}</p><p className="text-slate-500">Véhicule : {[data.delivery.vehicle, data.delivery.vehicle_plate].filter(Boolean).join(" · ") || "—"}</p></div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-slate-400 mb-1">Articles</p>
            <div className="space-y-1">
              {(data.order?.items || []).map((i, idx) => (
                <div key={idx} className="flex justify-between"><span className="text-slate-600">{i.quantity}× {i.name}</span><span className="font-num">{formatFCFA(i.price * i.quantity)}</span></div>
              ))}
              <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span className="font-num text-[#C85A32]">{formatFCFA(data.order?.total || 0)}</span></div>
            </div>
          </div>
          {data.delivery.proof && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="font-semibold text-green-800">Preuve de livraison</p>
              <p className="text-green-700 text-xs">Reçu par {data.delivery.proof.receiver_name} {data.delivery.proof.phone && `· ${data.delivery.proof.phone}`}</p>
              {data.delivery.proof.comment && <p className="text-green-700 text-xs">{data.delivery.proof.comment}</p>}
              {data.delivery.proof.photo && <img src={data.delivery.proof.photo} alt="" className="mt-2 w-24 h-24 object-cover rounded-lg" />}
            </div>
          )}
          <a href={`/bordereau/${id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#0F172A] text-white font-semibold text-sm px-4 py-2 rounded-lg" data-testid="detail-bordereau-link"><Printer className="w-4 h-4" /> Bordereau de livraison</a>
        </div>
      )}
    </Modal>
  );
}
